import { useEffect } from "react";

import { fetchFastApi } from "../api/fastapi";

const EMBED_SCRIPT_ID = "forethought-widget-embed-script";
const WIDGET_IFRAME_ID = "forethought-chat";
const EMBED_SCRIPT_SRC = "https://solve-widget.forethought.ai/embed.js";
// Opens every Midship-originated conversation on a dedicated Forethought intent
// instead of the default greeting (MS-3035). Not a secret: it names a workflow
// in Forethought's own configuration, so it is checked in rather than injected.
const INITIAL_INTENT_ID = "6a676164-78ec-4f5b-b20e-3176e509ff34";
// A hung /support/forethought-auth request must not block the widget from
// ever appearing, so we cap the wait and degrade to anonymous past this.
const AUTH_FETCH_TIMEOUT_MS = 4000;

declare global {
  interface Window {
    Forethought?: (
      selector: "widget",
      action: "open" | "close" | "hide" | "show" | "clearLocalData",
    ) => void;
  }
}

/**
 * Loads the Forethought Solve support widget (replacing Intercom) for the
 * lifetime of the component that calls this. We render that component only
 * for signed-in users, so the widget loads on sign-in and is cleaned up on
 * unmount (e.g. logout).
 *
 * Identity comes first: we fetch a signed user-context token and only then
 * inject the embed script, so `data-ft-user-data` is present when Forethought
 * initialises. That means the widget appears one API round trip later than it
 * used to, and `openForethoughtWidget()` is a no-op until it does. The fetch
 * is capped at AUTH_FETCH_TIMEOUT_MS and every failure is non-fatal: the
 * widget still loads, the conversation is just anonymous.
 *
 * The `hidden` attribute suppresses Forethought's built-in launcher on load,
 * and we re-hide it whenever the chat is closed, so the widget is opened
 * exclusively from the sidebar's Support link via `openForethoughtWidget()`.
 */
export function useForethoughtWidget() {
  useEffect(() => {
    const apiKey = import.meta.env.VITE_FORETHOUGHT_API_KEY;
    // Without a key the widget just 401s against Forethought's API, so skip it.
    if (!apiKey || document.getElementById(EMBED_SCRIPT_ID)) {
      return;
    }

    let cancelled = false;

    // Closing the chat leaves the iframe shown — which re-reveals Forethought's
    // launcher — so re-hide it on close to keep our button the sole entry.
    const onMessage = (event: MessageEvent) => {
      if (event.data?.event === "forethoughtWidgetClosed") {
        window.Forethought?.("widget", "hide");
      }
    };
    window.addEventListener("message", onMessage);

    const initWidget = async () => {
      // Identity for support (MS-3035): a signed JWT Forethought parses into
      // context variables. Non-fatal by design — on any failure the widget
      // still loads and the conversation is just anonymous, as it was before.
      let userDataToken: string | null = null;
      // Race against a timeout so a stalled backend (slow DB lookup, etc.)
      // can't leave the widget waiting forever — it degrades to anonymous
      // just like an outright fetch error does.
      const authTimeout = new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), AUTH_FETCH_TIMEOUT_MS);
      });
      try {
        const data = await Promise.race([
          fetchFastApi({
            method: "GET",
            path: "/support/forethought-auth",
          }),
          authTimeout,
        ]);
        userDataToken = data?.forethought_token ?? null;
      } catch {
        // widget still loads, just anonymous
      }

      // The effect may have been cleaned up (e.g. logout) while awaiting.
      if (cancelled || document.getElementById(EMBED_SCRIPT_ID)) {
        return;
      }

      const script = document.createElement("script");
      script.id = EMBED_SCRIPT_ID;
      script.src = EMBED_SCRIPT_SRC;
      script.setAttribute("data-api-key", apiKey);
      script.setAttribute("initial-intent-id", INITIAL_INTENT_ID);
      // Hidden on load so Forethought's own launcher never shows (any non-empty
      // value works). The sidebar's Support link is the only entry point.
      script.setAttribute("hidden", "true");
      if (userDataToken) {
        script.setAttribute("data-ft-user-data", userDataToken);
      }
      document.head.appendChild(script);
    };

    void initWidget();

    return () => {
      // Teardown on unmount (e.g. logout): clear the conversation, hide, and
      // drop the widget. `clearLocalData` is Forethought's documented logout
      // hook — removing the script tag alone does not unload a running widget.
      cancelled = true;
      window.removeEventListener("message", onMessage);
      window.Forethought?.("widget", "clearLocalData");
      window.Forethought?.("widget", "hide");
      document.getElementById(EMBED_SCRIPT_ID)?.remove();
      document.getElementById(WIDGET_IFRAME_ID)?.remove();
    };
  }, []);
}

/**
 * Opens the Solve widget from our custom launcher: `show` reveals the iframe
 * (hidden on load), `open` expands the chat. Both no-op until the embed script
 * has loaded.
 */
export function openForethoughtWidget() {
  window.Forethought?.("widget", "show");
  window.Forethought?.("widget", "open");
}
